import { ReactNode } from 'react';

import styles from './ParseHyperlinks.module.scss';

type Props = {
  message: string;
  style?: object;
};

// Detection patterns are intentionally NOT global (`/g`). A `/g` regex is
// stateful: `.test()` advances `lastIndex`, so reusing the same instance across
// tokens yields alternating true/false results. Anchored, flag-free patterns
// keep detection consistent for every token.
const urlRegex = /^https?:\/\/[^\s]+$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
const bareDomainRegex = /^[^\s]+\.(com|org|net)(\.[a-zA-Z]{2})?\/?[^\s]*$/i;

// Returns a safe http(s) href for the given value, or null when it does not
// resolve to a web link. This blocks `javascript:`, `data:` and similar schemes
// from ever reaching an anchor's `href`.
function toSafeHttpHref(value: string): string | null {
  try {
    const url = new URL(value);

    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.href;
    }
  } catch {
    // Not a parseable URL — fall through and treat as plain text.
  }

  return null;
}

function renderToken(token: string, key: number): ReactNode {
  // Whitespace separators (and empty leading/trailing chunks) are rendered
  // verbatim. As React string children they are automatically escaped.
  if (!token || /^\s+$/.test(token)) {
    return token;
  }

  if (urlRegex.test(token)) {
    const href = toSafeHttpHref(token);

    if (href) {
      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {token}
        </a>
      );
    }
  } else if (emailRegex.test(token)) {
    return (
      <a key={key} href={`mailto:${token}`}>
        {token}
      </a>
    );
  } else if (bareDomainRegex.test(token)) {
    const href = toSafeHttpHref(`https://${token}`);

    if (href) {
      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {token}
        </a>
      );
    }
  }

  // Plain text, or a token that failed URL validation. React escapes it, so any
  // markup in the (untrusted) message is rendered inert as literal text.
  return token;
}

export function ParseHyperlinks({ message, style = {} }: Props) {
  if (!message) {
    return null;
  }

  const content = message.split('\n').map((line, lineIndex) => {
    // Split on whitespace while keeping the separators as their own tokens, so
    // words stay separated without ever concatenating tokens back into an HTML
    // string. (Runs of whitespace collapse per normal HTML rendering, exactly as
    // the previous dangerouslySetInnerHTML implementation did.)
    const tokens = line.split(/(\s+)/);

    return (
      <p className={styles.container} key={lineIndex} style={style}>
        {tokens.map((token, tokenIndex) => renderToken(token, tokenIndex))}
      </p>
    );
  });

  return <>{content}</>;
}
